/**
 * Script Intelligence Dashboard - Hero feature for instant script analysis
 * Shows risk radar, quality scorecard, film parallels, and production estimates
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAnalysis, runAnalysis, estimateProduction, estimateMarket } from "@/services/analysisService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle, TrendingUp, Zap, Film, Target, Users, 
  Clock, DollarSign, MapPin, Sparkles, BarChart3, Brain,
  ChevronDown, ChevronUp, Loader2, RefreshCw, CheckCircle,
  XCircle, AlertCircle, ThumbsUp, ThumbsDown, Minus, Camera,
  Clapperboard, IndianRupee, Calendar, Video
} from "lucide-react";


const RISK_LEVELS = {
  low: { color: "text-green-400", bg: "bg-green-500/20", icon: CheckCircle },
  medium: { color: "text-yellow-400", bg: "bg-yellow-500/20", icon: AlertCircle },
  high: { color: "text-orange-400", bg: "bg-orange-500/20", icon: AlertTriangle },
  very_high: { color: "text-red-400", bg: "bg-red-500/20", icon: XCircle }
};

// Score gradient colors
const getScoreColor = (score) => {
  if (score >= 80) return "text-green-400";
  if (score >= 60) return "text-yellow-400";
  if (score >= 40) return "text-orange-400";
  return "text-red-400";
};

const getScoreIcon = (score) => {
  if (score >= 70) return ThumbsUp;
  if (score >= 40) return Minus;
  return ThumbsDown;
};

// Circular progress component
function ScoreCircle({ score, label, size = 60 }) {
  const circumference = 2 * Math.PI * 20;
  const offset = circumference - (score / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle cx={size/2} cy={size/2} r="20" fill="none" stroke="var(--bg-elevated)" strokeWidth="4" />
          <circle 
            cx={size/2} cy={size/2} r="20" fill="none" 
            stroke={score >= 70 ? "#22c55e" : score >= 40 ? "#eab308" : "#ef4444"} 
            strokeWidth="4" 
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-sm font-bold ${getScoreColor(score)}`}>
          {score}
        </span>
      </div>
      <span className="text-[9px] text-[var(--text-muted)] text-center max-w-[60px]">{label}</span>
    </div>
  );
}

// Collapsible section
function Section({ title, icon: Icon, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-[var(--border-subtle)] rounded-lg overflow-hidden">
      <button 
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 bg-[var(--bg-elevated)] hover:bg-[var(--bg-surface)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-[var(--color-gold-400)]" />
          <span className="text-xs font-medium text-[var(--text-primary)]">{title}</span>
          {badge && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]">{badge}</span>}
        </div>
        {open ? <ChevronUp size={14} className="text-[var(--text-muted)]" /> : <ChevronDown size={14} className="text-[var(--text-muted)]" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-3 border-t border-[var(--border-subtle)]">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ScriptIntelligence({ projectId, scriptContent, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [productionEstimate, setProductionEstimate] = useState(null);
  const [marketEstimate, setMarketEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProduction, setLoadingProduction] = useState(false);
  const [loadingMarket, setLoadingMarket] = useState(false);
  const [error, setError] = useState(null);
  const [budgetTier, setBudgetTier] = useState("standard");
  const [releaseWindow, setReleaseWindow] = useState("regular");
  const [castInput, setCastInput] = useState("");
  const [budgetInput, setBudgetInput] = useState("50");
  
  useEffect(() => {
    if (projectId) {
      loadExistingAnalysis();
    }
  }, [projectId]);
  
  const loadExistingAnalysis = async () => {
    try {
      const data = await getAnalysis(projectId);
      if (data?.script_analysis) setAnalysis(data.script_analysis);
      if (data?.production_estimate) setProductionEstimate(data.production_estimate);
      if (data?.market_estimate) setMarketEstimate(data.market_estimate);
    } catch {
      // No existing analysis
    }
  };
  
  const handleRunAnalysis = async () => {
    if (!scriptContent || scriptContent.length < 100) {
      toast.error("Script content too short for analysis");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await runAnalysis({
        project_id: projectId,
        script_content: scriptContent
      });
      
      setAnalysis(data);
      toast.success("Script analysis complete!");
    } catch (err) {
      setError(err.response?.data?.detail || "Analysis failed");
      toast.error("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const runProductionEstimate = async () => {
    if (!analysis) {
      toast.error("Run script analysis first");
      return;
    }
    
    setLoadingProduction(true);
    try {
      const data = await estimateProduction({
        project_id: projectId,
        budget_tier: budgetTier,
        include_post: false
      });
      
      setProductionEstimate(data);
      toast.success("Production estimate ready!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Estimation failed");
    } finally {
      setLoadingProduction(false);
    }
  };

  const runMarketEstimate = async () => {
    if (!analysis) {
      toast.error("Run script analysis first");
      return;
    }
    
    setLoadingMarket(true);
    try {
      const cast = castInput.split(',').map(c => c.trim()).filter(c => c);
      const data = await estimateMarket({
        project_id: projectId,
        release_window: releaseWindow,
        budget_cr: parseFloat(budgetInput) || 50,
        cast
      });
      
      setMarketEstimate(data);
      toast.success("Market estimate ready!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Estimation failed");
    } finally {
      setLoadingMarket(false);
    }
  };

  // Format currency in lakhs/crores
  const formatINR = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };
  
  // Format crores display
  const formatCr = (amount) => `₹${amount}Cr`;
  
  if (!analysis && !loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center">
        <Brain size={40} className="text-[var(--color-gold-400)] mb-4" />
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Script Intelligence</h3>
        <p className="text-xs text-[var(--text-muted)] mb-6 max-w-[280px]">
          Instantly analyze your script for budget risks, story quality, and market potential.
        </p>
        <Button onClick={handleRunAnalysis} className="btn-primary" disabled={!scriptContent} data-testid="run-analysis-btn">
          <Zap size={15} />
          <span className="ml-2">Analyze Script</span>
        </Button>
        {error && <p className="text-xs text-red-400 mt-4">{error}</p>}
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8">
        <Loader2 size={32} className="animate-spin text-[var(--color-gold-400)] mb-4" />
        <p className="text-sm text-[var(--text-primary)]">Analyzing script...</p>
        <p className="text-[10px] text-[var(--text-muted)] mt-1">This takes 10-20 seconds</p>
      </div>
    );
  }
  
  const risk = analysis?.risk_assessment || {};
  const quality = analysis?.quality_scorecard || {};
  const genre = analysis?.genre_classification || {};
  const parallels = analysis?.film_parallels || [];
  const breakdown = analysis?.scene_breakdown || {};
  const commercial = analysis?.commercial_viability || {};
  
  const RiskIcon = RISK_LEVELS[risk.budget_risk_level]?.icon || AlertCircle;
  const riskStyle = RISK_LEVELS[risk.budget_risk_level] || RISK_LEVELS.medium;

  return (
    <div className="h-full flex flex-col" data-testid="script-intelligence-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-[var(--color-gold-400)]" />
          <span className="text-sm font-semibold text-[var(--text-primary)]">Script Intelligence</span>
          {analysis?.detected_language && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-[rgba(20,184,166,0.2)] text-[var(--color-teal-400)] uppercase">
              {analysis.detected_language}
            </span>
          )}
        </div>
        <Button onClick={handleRunAnalysis} size="sm" className="btn-ghost text-xs" disabled={loading}>
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          <span className="ml-1">Refresh</span>
        </Button>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Risk Radar - Hero Section */}
          <div className={`p-4 rounded-lg border ${riskStyle.bg} border-[var(--border-subtle)]`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <RiskIcon size={18} className={riskStyle.color} />
                <span className="text-sm font-semibold text-[var(--text-primary)]">Budget Risk</span>
              </div>
              <span className={`text-lg font-bold ${riskStyle.color}`}>
                {risk.budget_risk_level?.toUpperCase().replace('_', ' ')}
              </span>
            </div>
            
            <div className="grid grid-cols-3 gap-3 text-center mb-3">
              <div className="bg-[var(--bg-overlay)] p-2 rounded">
                <p className="text-lg font-bold text-[var(--text-primary)]">{risk.total_estimated_shoot_days || '?'}</p>
                <p className="text-[9px] text-[var(--text-muted)]">Shoot Days</p>
              </div>
              <div className="bg-[var(--bg-overlay)] p-2 rounded">
                <p className="text-lg font-bold text-[var(--text-primary)]">₹{risk.recommended_budget_range_cr?.min || '?'}Cr</p>
                <p className="text-[9px] text-[var(--text-muted)]">Min Budget</p>
              </div>
              <div className="bg-[var(--bg-overlay)] p-2 rounded">
                <p className="text-lg font-bold text-[var(--text-primary)]">₹{risk.recommended_budget_range_cr?.max || '?'}Cr</p>
                <p className="text-[9px] text-[var(--text-muted)]">Max Budget</p>
              </div>
            </div>
            
            {/* Risk factors */}
            {risk.risk_factors?.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-medium text-[var(--text-muted)]">HIGH RISK SCENES:</p>
                {risk.risk_factors.filter(f => f.severity === 'high').slice(0, 3).map((factor, i) => (
                  <div key={i} className="flex items-start gap-2 bg-[var(--bg-overlay)] p-2 rounded text-[10px]">
                    <AlertTriangle size={12} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[var(--text-primary)] font-medium">Scene {factor.scene_range}:</span>
                      <span className="text-[var(--text-muted)] ml-1">{factor.description}</span>
                      <span className="text-orange-400 ml-1">(+{factor.estimated_cost_impact_percent}% cost)</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Quality Scorecard */}
          <Section title="Story Quality" icon={Target} badge={`${quality.overall_score || 0}/100`}>
            <div className="flex justify-around mb-4">
              <ScoreCircle score={quality.conflict_strength?.score || 0} label="Conflict" />
              <ScoreCircle score={quality.character_arcs?.score || 0} label="Arcs" />
              <ScoreCircle score={quality.pacing?.score || 0} label="Pacing" />
              <ScoreCircle score={quality.dialogue_quality?.score || 0} label="Dialogue" />
              <ScoreCircle score={quality.theme_coherence?.score || 0} label="Theme" />
            </div>
            
            {/* Detailed analysis */}
            <div className="space-y-2 text-[10px]">
              {quality.conflict_strength?.analysis && (
                <p className="text-[var(--text-muted)]">
                  <span className="text-[var(--text-primary)] font-medium">Conflict:</span> {quality.conflict_strength.analysis}
                </p>
              )}
              {quality.character_arcs?.analysis && (
                <p className="text-[var(--text-muted)]">
                  <span className="text-[var(--text-primary)] font-medium">Arcs:</span> {quality.character_arcs.analysis}
                </p>
              )}
              {quality.emotional_beats?.analysis && (
                <p className="text-[var(--text-muted)]">
                  <span className="text-[var(--text-primary)] font-medium">Emotional Beats:</span> {quality.emotional_beats.analysis}
                </p>
              )}
            </div>
            
            {/* Themes */}
            {quality.theme_coherence?.identified_themes?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {quality.theme_coherence.identified_themes.map((theme, i) => (
                  <span key={i} className="text-[9px] px-2 py-0.5 rounded-full bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]">
                    {theme}
                  </span>
                ))}
              </div>
            )}
          </Section>
          
          {/* Film Parallels */}
          <Section title="Film Parallels" icon={Film} badge={`${parallels.length} matches`}>
            <div className="space-y-2">
              {parallels.map((film, i) => (
                <div key={i} className="bg-[var(--bg-elevated)] p-2.5 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-[var(--text-primary)]">{film.title} ({film.year})</span>
                    <span className="text-[9px] text-[var(--color-teal-400)]">{film.theme_match_percent}% match</span>
                  </div>
                  <div className="flex items-center gap-3 text-[9px] text-[var(--text-muted)]">
                    <span>Budget: ₹{film.budget_cr}Cr</span>
                    <span>Box Office: ₹{film.box_office_cr}Cr</span>
                    <span className={film.roi_percent > 100 ? "text-green-400" : "text-yellow-400"}>
                      ROI: {film.roi_percent}%
                    </span>
                  </div>
                  {film.matching_themes?.length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {film.matching_themes.map((t, j) => (
                        <span key={j} className="text-[8px] px-1.5 py-0.5 rounded bg-[var(--bg-surface)] text-[var(--text-muted)]">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
          
          {/* Commercial Viability */}
          <Section title="Commercial Viability" icon={TrendingUp} defaultOpen={false}>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--bg-elevated)] p-2.5 rounded text-center">
                <p className="text-lg font-bold text-[var(--text-primary)]">{commercial.mass_appeal_score || 0}</p>
                <p className="text-[9px] text-[var(--text-muted)]">Mass Appeal</p>
              </div>
              <div className="bg-[var(--bg-elevated)] p-2.5 rounded text-center">
                <p className="text-lg font-bold text-[var(--text-primary)]">{commercial.repeat_value_score || 0}</p>
                <p className="text-[9px] text-[var(--text-muted)]">Repeat Value</p>
              </div>
            </div>
            <div className="mt-3 text-[10px] space-y-1">
              <p className="text-[var(--text-muted)]">
                <span className="font-medium text-[var(--text-primary)]">Word of Mouth:</span> {commercial.word_of_mouth_potential || 'N/A'}
              </p>
              <p className="text-[var(--text-muted)]">
                <span className="font-medium text-[var(--text-primary)]">Opening Weekend:</span> {commercial.opening_weekend_strength || 'N/A'}
              </p>
            </div>
          </Section>
          
          {/* Scene Breakdown */}
          <Section title="Scene Breakdown" icon={BarChart3} defaultOpen={false}>
            <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
              <div className="bg-[var(--bg-elevated)] p-2 rounded">
                <p className="font-bold text-[var(--text-primary)]">{breakdown.total_scenes || 0}</p>
                <p className="text-[var(--text-muted)]">Total Scenes</p>
              </div>
              <div className="bg-[var(--bg-elevated)] p-2 rounded">
                <p className="font-bold text-[var(--text-primary)]">{breakdown.day_scenes || 0}</p>
                <p className="text-[var(--text-muted)]">Day Scenes</p>
              </div>
              <div className="bg-[var(--bg-elevated)] p-2 rounded">
                <p className="font-bold text-[var(--text-primary)]">{breakdown.night_scenes || 0}</p>
                <p className="text-[var(--text-muted)]">Night Scenes</p>
              </div>
              <div className="bg-[var(--bg-elevated)] p-2 rounded">
                <p className="font-bold text-[var(--text-primary)]">{breakdown.interior_count || 0}</p>
                <p className="text-[var(--text-muted)]">Interior</p>
              </div>
              <div className="bg-[var(--bg-elevated)] p-2 rounded">
                <p className="font-bold text-[var(--text-primary)]">{breakdown.exterior_count || 0}</p>
                <p className="text-[var(--text-muted)]">Exterior</p>
              </div>
              <div className="bg-[var(--bg-elevated)] p-2 rounded">
                <p className="font-bold text-[var(--text-primary)]">{breakdown.unique_locations || 0}</p>
                <p className="text-[var(--text-muted)]">Locations</p>
              </div>
            </div>
          </Section>
          
          {/* Genre & Target */}
          <div className="flex gap-2 text-[10px]">
            <span className="px-2 py-1 rounded bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]">
              {genre.primary_genre}
            </span>
            <span className="px-2 py-1 rounded bg-[var(--bg-elevated)] text-[var(--text-muted)]">
              {genre.tone}
            </span>
            <span className="px-2 py-1 rounded bg-[var(--bg-elevated)] text-[var(--text-muted)]">
              {genre.target_audience} audience
            </span>
          </div>
          
          {/* Production Estimator */}
          <Section title="Production Estimate" icon={Clapperboard} badge={productionEstimate ? formatINR(productionEstimate.budget_summary?.grand_total) : null}>
            {!productionEstimate ? (
              <div className="text-center py-4">
                <p className="text-[10px] text-[var(--text-muted)] mb-3">
                  Calculate shoot days, equipment costs, and crew requirements.
                </p>
                <div className="flex gap-2 justify-center mb-3">
                  {["basic", "standard", "premium"].map(tier => (
                    <button
                      key={tier}
                      onClick={() => setBudgetTier(tier)}
                      className={`text-[9px] px-2.5 py-1 rounded-full transition-all ${
                        budgetTier === tier 
                          ? "bg-[var(--color-gold-500)] text-black font-medium" 
                          : "bg-[var(--bg-elevated)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
                      }`}
                    >
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </button>
                  ))}
                </div>
                <Button onClick={runProductionEstimate} size="sm" className="btn-secondary text-xs" disabled={loadingProduction} data-testid="run-production-estimate-btn">
                  {loadingProduction ? <Loader2 size={12} className="animate-spin" /> : <IndianRupee size={12} />}
                  <span className="ml-1.5">Calculate Budget</span>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Shoot Schedule */}
                <div className="bg-[var(--bg-elevated)] p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-medium text-[var(--text-primary)] flex items-center gap-1.5">
                      <Calendar size={11} className="text-[var(--color-teal-400)]" />
                      Shoot Schedule
                    </span>
                    <span className="text-sm font-bold text-[var(--color-teal-400)]">
                      {productionEstimate.shoot_schedule?.total_days} days
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 text-[9px]">
                    <div className="bg-[var(--bg-surface)] p-1.5 rounded text-center">
                      <p className="font-medium text-[var(--text-primary)]">{productionEstimate.shoot_schedule?.day_shoots}</p>
                      <p className="text-[var(--text-muted)]">Day</p>
                    </div>
                    <div className="bg-[var(--bg-surface)] p-1.5 rounded text-center">
                      <p className="font-medium text-[var(--text-primary)]">{productionEstimate.shoot_schedule?.night_shoots}</p>
                      <p className="text-[var(--text-muted)]">Night</p>
                    </div>
                    <div className="bg-[var(--bg-surface)] p-1.5 rounded text-center">
                      <p className="font-medium text-[var(--text-primary)]">{productionEstimate.shoot_schedule?.location_count}</p>
                      <p className="text-[var(--text-muted)]">Locations</p>
                    </div>
                  </div>
                </div>
                
                {/* Shot Summary */}
                <div className="flex gap-2 text-[9px]">
                  <div className="flex-1 bg-[var(--bg-elevated)] p-2 rounded text-center">
                    <p className="font-bold text-[var(--text-primary)]">{productionEstimate.shot_summary?.total_anchors}</p>
                    <p className="text-[var(--text-muted)]">Shot Anchors</p>
                  </div>
                  <div className="flex-1 bg-[var(--bg-elevated)] p-2 rounded text-center">
                    <p className="font-bold text-[var(--text-primary)]">{productionEstimate.shot_summary?.total_shots_generated}</p>
                    <p className="text-[var(--text-muted)]">Shots Generated</p>
                  </div>
                  <div className="flex-1 bg-[var(--bg-elevated)] p-2 rounded text-center">
                    <p className="font-bold text-[var(--text-primary)]">{productionEstimate.shot_summary?.complex_shots}</p>
                    <p className="text-[var(--text-muted)]">Complex</p>
                  </div>
                </div>
                
                {/* Budget Breakdown */}
                <div className="bg-[var(--bg-elevated)] p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-medium text-[var(--text-primary)] flex items-center gap-1.5">
                      <IndianRupee size={11} className="text-[var(--color-gold-400)]" />
                      Budget Breakdown
                    </span>
                    <span className="text-[9px] px-2 py-0.5 rounded bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]">
                      {productionEstimate.budget_tier}
                    </span>
                  </div>
                  <div className="space-y-1.5 text-[9px]">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Equipment</span>
                      <span className="text-[var(--text-primary)]">{formatINR(productionEstimate.budget_summary?.equipment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Crew</span>
                      <span className="text-[var(--text-primary)]">{formatINR(productionEstimate.budget_summary?.crew)}</span>
                    </div>
                    <div className="flex justify-between border-t border-[var(--border-subtle)] pt-1">
                      <span className="text-[var(--text-muted)]">Subtotal</span>
                      <span className="text-[var(--text-primary)]">{formatINR(productionEstimate.budget_summary?.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Contingency ({productionEstimate.budget_summary?.contingency_percent}%)</span>
                      <span className="text-[var(--text-primary)]">{formatINR(productionEstimate.budget_summary?.contingency)}</span>
                    </div>
                    <div className="flex justify-between border-t border-[var(--border-subtle)] pt-1.5 mt-1.5">
                      <span className="text-[var(--text-primary)] font-semibold">TOTAL</span>
                      <span className="text-[var(--color-gold-400)] font-bold">{formatINR(productionEstimate.budget_summary?.grand_total)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Equipment Details */}
                {productionEstimate.equipment && (
                  <div className="text-[9px] space-y-1">
                    <p className="font-medium text-[var(--text-muted)] mb-1.5">Equipment Package:</p>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">{productionEstimate.equipment.camera?.name}</span>
                      <span className="text-[var(--text-primary)]">{formatINR(productionEstimate.equipment.camera?.total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">{productionEstimate.equipment.lighting?.name}</span>
                      <span className="text-[var(--text-primary)]">{formatINR(productionEstimate.equipment.lighting?.total)}</span>
                    </div>
                  </div>
                )}
                
                {/* Recalculate button */}
                <div className="pt-2 border-t border-[var(--border-subtle)]">
                  <div className="flex gap-2 justify-center mb-2">
                    {["basic", "standard", "premium"].map(tier => (
                      <button
                        key={tier}
                        onClick={() => setBudgetTier(tier)}
                        className={`text-[8px] px-2 py-0.5 rounded-full transition-all ${
                          budgetTier === tier 
                            ? "bg-[var(--color-gold-500)] text-black font-medium" 
                            : "bg-[var(--bg-surface)] text-[var(--text-muted)]"
                        }`}
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                  <Button onClick={runProductionEstimate} size="sm" className="btn-ghost text-[9px] w-full" disabled={loadingProduction}>
                    {loadingProduction ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                    <span className="ml-1">Recalculate</span>
                  </Button>
                </div>
              </div>
            )}
          </Section>
          
          {/* Market Intelligence */}
          <Section title="Market Intelligence" icon={TrendingUp} badge={marketEstimate ? formatCr(marketEstimate.total_estimate_cr) : null}>
            {!marketEstimate ? (
              <div className="space-y-3">
                <p className="text-[10px] text-[var(--text-muted)]">
                  Estimate box office potential across regions based on script language, genre, and cast.
                </p>
                
                {/* Release Window */}
                <div>
                  <label className="text-[9px] text-[var(--text-muted)] block mb-1.5">Release Window</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { value: "regular", label: "Regular" },
                      { value: "holiday", label: "Holiday (+30%)" },
                      { value: "festival", label: "Festival (+50%)" },
                      { value: "clash", label: "Clash (-30%)" }
                    ].map(w => (
                      <button
                        key={w.value}
                        onClick={() => setReleaseWindow(w.value)}
                        className={`text-[8px] px-2 py-1 rounded transition-all ${
                          releaseWindow === w.value
                            ? "bg-[var(--color-teal-500)] text-black font-medium"
                            : "bg-[var(--bg-elevated)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
                        }`}
                      >
                        {w.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Budget Input */}
                <div>
                  <label className="text-[9px] text-[var(--text-muted)] block mb-1.5">Production Budget (Cr)</label>
                  <input
                    type="number"
                    value={budgetInput}
                    onChange={(e) => setBudgetInput(e.target.value)}
                    className="w-full bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded px-2 py-1.5 text-[10px] text-[var(--text-primary)]"
                    placeholder="50"
                  />
                </div>
                
                {/* Cast Input */}
                <div>
                  <label className="text-[9px] text-[var(--text-muted)] block mb-1.5">Cast (comma-separated, optional)</label>
                  <input
                    type="text"
                    value={castInput}
                    onChange={(e) => setCastInput(e.target.value)}
                    className="w-full bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded px-2 py-1.5 text-[10px] text-[var(--text-primary)]"
                    placeholder="Actor 1, Actor 2..."
                  />
                </div>
                
                <Button onClick={runMarketEstimate} size="sm" className="btn-secondary text-xs w-full" disabled={loadingMarket} data-testid="run-market-estimate-btn">
                  {loadingMarket ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
                  <span className="ml-1.5">Estimate Box Office</span>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Total Estimate */}
                <div className="bg-gradient-to-r from-[rgba(20,184,166,0.2)] to-[rgba(212,168,83,0.2)] p-3 rounded-lg border border-[rgba(20,184,166,0.3)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-[var(--text-muted)]">Estimated Box Office</span>
                    <span className="text-[9px] px-2 py-0.5 rounded bg-[var(--bg-surface)] text-[var(--text-muted)]">
                      {marketEstimate.detected_language?.toUpperCase()} • {marketEstimate.genre}
                    </span>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[var(--color-teal-400)]">
                      {formatCr(marketEstimate.total_estimate_cr)}
                    </p>
                    <p className="text-[9px] text-[var(--text-muted)] mt-1">
                      Range: {formatCr(marketEstimate.estimate_range_cr?.low)} - {formatCr(marketEstimate.estimate_range_cr?.high)}
                    </p>
                  </div>
                  <div className="flex justify-around mt-3 pt-2 border-t border-[var(--border-subtle)]">
                    <div className="text-center">
                      <p className={`text-sm font-bold ${marketEstimate.roi_estimate_percent > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {marketEstimate.roi_estimate_percent > 0 ? '+' : ''}{marketEstimate.roi_estimate_percent}%
                      </p>
                      <p className="text-[8px] text-[var(--text-muted)]">Est. ROI</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-[var(--text-primary)]">
                        {marketEstimate.release_window_multiplier}x
                      </p>
                      <p className="text-[8px] text-[var(--text-muted)]">{marketEstimate.release_window}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-[var(--text-primary)]">
                        {marketEstimate.confidence_level}
                      </p>
                      <p className="text-[8px] text-[var(--text-muted)]">Confidence</p>
                    </div>
                  </div>
                </div>
                
                {/* Regional Breakdown */}
                <div>
                  <p className="text-[10px] font-medium text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
                    <MapPin size={11} />
                    Regional Breakdown
                  </p>
                  <div className="space-y-1.5">
                    {Object.entries(marketEstimate.regional_details || {}).slice(0, 8).map(([key, region]) => (
                      <div key={key} className="flex items-center gap-2 bg-[var(--bg-elevated)] p-2 rounded">
                        <div className="flex-1">
                          <p className="text-[10px] text-[var(--text-primary)]">{region.name}</p>
                          <div className="w-full h-1.5 bg-[var(--bg-surface)] rounded-full mt-1 overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[var(--color-teal-500)] to-[var(--color-gold-500)] rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(region.market_share_percent * 2, 100)}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-medium text-[var(--color-gold-400)]">
                            {formatCr(marketEstimate.regional_breakdown?.[key] || 0)}
                          </p>
                          <p className="text-[8px] text-[var(--text-muted)]">
                            {region.market_share_percent}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Comparable Films */}
                {marketEstimate.comparable_films?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-medium text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
                      <Film size={11} />
                      Comparable Films Performance
                    </p>
                    <div className="space-y-1.5">
                      {marketEstimate.comparable_films.map((film, i) => (
                        <div key={i} className="flex items-center justify-between bg-[var(--bg-elevated)] p-2 rounded text-[9px]">
                          <span className="text-[var(--text-primary)]">{film.title} ({film.year})</span>
                          <div className="flex items-center gap-3">
                            <span className="text-[var(--text-muted)]">{formatCr(film.box_office_cr)}</span>
                            <span className={film.roi_percent > 100 ? "text-green-400" : "text-yellow-400"}>
                              {film.roi_percent}% ROI
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Cast Considered */}
                {marketEstimate.cast_considered?.length > 0 && (
                  <div className="text-[9px] text-[var(--text-muted)]">
                    <span className="font-medium">Cast considered:</span> {marketEstimate.cast_considered.join(", ")}
                    <span className="text-[var(--color-teal-400)] ml-1">(+{((marketEstimate.star_power_multiplier - 1) * 100).toFixed(0)}% boost)</span>
                  </div>
                )}
                
                {/* Recalculate */}
                <div className="pt-2 border-t border-[var(--border-subtle)]">
                  <Button onClick={runMarketEstimate} size="sm" className="btn-ghost text-[9px] w-full" disabled={loadingMarket}>
                    {loadingMarket ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                    <span className="ml-1">Recalculate</span>
                  </Button>
                </div>
                
                {/* Disclaimer */}
                <p className="text-[8px] text-[var(--text-disabled)] italic">
                  {marketEstimate.disclaimer}
                </p>
              </div>
            )}
          </Section>
        </div>
      </ScrollArea>
    </div>
  );
}
